import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import {
  getUsage, recordQuestion, markNudged, remaining,
  counterLabel, isPureGreeting, isLowEffort, COPY,
} from "@/lib/askSam";
import { useTerminalSound } from "@/lib/useTerminalSound";

const PROMPT = "sam@erpik:~$";

const BOOT_MESSAGE = [
  " ___   _   __  __    ___ ___ ___ ___ _  __",
  "/ __| /_\\ |  \\/  |  | __| _ \\ _ \\_ _| |/ /",
  "\\__ \\/ _ \\| |\\/| |  | _||   /  _/| || ' < ",
  "|___/_/ \\_\\_|  |_|  |___|_|_\\_|  |___|_|\\_\\",
  "",
  "NODE_2026 · ACCESS GRANTED · PHOSPHOR ONLINE",
  "──────────────────────────────────────────────",
  "Sam Erpik OS  v2026.1.0-lts",
  "Technical Founder · Product Leader · Builder",
  "UK · open for senior IC, founding-engineer, fractional CTO",
  "",
  "> session started.",
  "",
  "Type 'help' for commands. Or ask me anything.",
  "tab autocompletes  ·  ↑ recalls history  ·  esc closes",
  "",
].join("\n");

// First two transcript lines: the boot banner + an AI greeting. `showNudge`
// adds the one-time "real AI" framing on a visitor's first ever open.
function initialHistory(showNudge) {
  const greeting = showNudge
    ? COPY.greeting + "\n\n" + COPY.nudge
    : COPY.greeting;
  return [
    { type: "system", text: BOOT_MESSAGE },
    { type: "greeting", text: greeting },
  ];
}

const COMMANDS = {
  help: () => [
    "┌──────────────────────────────────────────────────┐",
    "│  Commands                                        │",
    "├──────────────────────────────────────────────────┤",
    "│  about               → who's typing this        │",
    "│  ventures            → things I've built        │",
    "│  work                → Purple Luna case study   │",
    "│  lab                 → current experiments      │",
    "│  writing             → essays & notes           │",
    "│  presence            → am I at my desk?         │",
    "│  ls skills           → tech stack               │",
    "│  uptime              → career stats             │",
    "│  contact             → email me                 │",
    "│  hire sam            → make a good call         │",
    "│  cat                 → 🐱                       │",
    "│  sudo rm -rf /       → go on, try it            │",
    "│  clear               → wipe the slate           │",
    "│  exit                → close terminal           │",
    "├──────────────────────────────────────────────────┤",
    "│  ...or just ask me anything. I'm all yours.     │",
    "└──────────────────────────────────────────────────┘",
  ].join("\n"),

  about: () =>
    "Sam Erpik.\n\nTechnical founder. Product leader. Builder of things that ship.\n\nFounder of Purple Luna — an AI receptionist for small businesses.\nProduct & UX contract work for Canada Life UK — design system owner, design ↔ code sync, a new design-to-dev handover tool, modernising delivery.\nCo-founded QuantumEye (2025–2026) — edge-AI retail security. Still writing about and experimenting with computer vision.\n10+ years designing products — ERP, SaaS, IoT, financial services.\nMSc Management & Entrepreneurship · BSc (Hons) Informatics: Games & Multimedia Environments · Based in the UK.\n\nI build AI that answers, sees and decides. Not slowly.",

  ventures: () =>
    "Purple Luna   [ACTIVE]\n  AI receptionist for small businesses. WhatsApp receptionist live with clinic clients.\n  Answers, books appointments, takes deposits — 24/7.\n  Luna Voice (phone receptionist): built, in testing. Waitlist open.\n\nQuantumEye    [2025–2026]\n  Co-founded. Edge AI retail security: detection in under 3.9 seconds, fully on-device.\n  No footage left the store. Taken into retail pilots.\n\nWhotNow       [GOOD LESSONS]\n  Real-time nightlife discovery. Brighton & Kent.\n  30K monthly actives at peak. Real education in PMF.",

  lab: () =>
    "[ SHIPPED ]  GitHub Standup Agent\n             compared repos vs what the team actually said in standups\n\n[ LIVE ]     Writing Cursor Rules\n             teaching AI to code like a human would review it\n\n[ LIVE ]     Cat Instagram Bot\n             Eva Luna's autopilot. growing on its own. don't ask.\n\n[ BUILDING ] Jarvis\n             personal AI coding assistant. handles tasks so I can think.\n\n[ BUILDING ] SaaS Agent Suite\n             analysis, PR, project mgmt, testing — all agents, all async\n\n[ BUILDING ] Figma Plugin\n             bridging design tokens and production code",

  "ls skills": () =>
    "Languages & Frameworks\n  React  TypeScript  Python  Next.js  Node.js  Three.js\n\nData & Infra\n  PostgreSQL  Firebase  AWS  Docker  Edge AI  Computer Vision\n\nDesign\n  Figma  Framer  Design Systems  UX Strategy\n\nProduct\n  GTM Strategy  Investor Decks  Market Sizing  Team Leadership\n\nOther\n  Claude API  MCP  LangChain  CI/CD  Cursor Rules",

  uptime: () =>
    "System statistics:\n\n  Experience    10+ years\n  Ventures      3 built  (1 active, 2 past)\n  Products      too many to list\n  Clients       Canada Life UK · +(lots)\n  Cats          1  (Eva Luna, Chief Vibe Officer)\n  Coffee        ∞\n\n[████████████████████░░] still compiling",

  "hire sam": () => ({ text: "Good call.\n\nOpening the contact form in 1 second...", action: "contact" }),

  contact: () => ({ text: "Opening contact form...", action: "contact" }),

  work: () => ({
    text: "Opening Purple Luna case study...",
    action: "navigate",
    to: "/case/purpleluna",
  }),

  writing: () => ({
    text: "Opening /writing...",
    action: "navigate",
    to: "/writing",
  }),

  presence: async () => {
    try {
      const res = await fetch("/.netlify/functions/presence", { cache: "no-store" });
      if (!res.ok) throw new Error(`presence ${res.status}`);
      const data = await res.json();
      const seenAt = data.lastSeenAt
        ? new Date(data.lastSeenAt).toLocaleString("en-GB", {
            timeZone: "Europe/London",
            weekday: "short", hour: "2-digit", minute: "2-digit",
          }) + " GMT"
        : "—";
      const lines = [
        `state        ${data.state}`,
        `label        ${data.label}`,
        `last ping    ${seenAt}`,
        `override     ${data.overridden ? "active" : "none"}`,
      ];
      return lines.join("\n");
    } catch (e) {
      return `presence endpoint unreachable (${e.message}).`;
    }
  },

  cat: () =>
    "       /\\_____/\\\n      /  o   o  \\\n     ( ==  ^  == )\n      )         (\n     (           )\n    ( (  )   (  ) )\n   (__(__)___(__)__)\n\n   Eva Luna\n   Chief Vibe Officer, erpik.com\n   Instagram: @the.evaluna.cat   <- follow her, I'm begging\n   Status: asleep on the keyboard, ignoring my pleas\n\n   Every day I hold up a camera and beg her to do one cute thing\n   so she goes viral and I can finally quit my job. She remains\n   professionally unbothered. You could change that:\n   instagram.com/the.evaluna.cat",

  quit: () =>
    "$ quit\nCan't. Eva Luna isn't famous yet.\n\nFollow @the.evaluna.cat to accelerate my retirement:\ninstagram.com/the.evaluna.cat",

  "quit job": () =>
    "$ quit job\nCan't. Eva Luna isn't famous yet.\n\nFollow @the.evaluna.cat to accelerate my retirement:\ninstagram.com/the.evaluna.cat",

  "sudo rm -rf /": () =>
    "$ sudo rm -rf /\nChecking permissions...\n\nPermission denied.\n\nThe site is still here. Nice try.",

  "sudo rm -rf competitors": () =>
    "$ sudo rm -rf competitors\nPassword: ••••••••\nChecking permissions...\n\nPermission denied.\n\nThey're already gone.",

  sudo: () => "Nice try. This isn't that kind of terminal.",
  ls: () => "Try 'ls skills' for a full list.",
  pwd: () => "/home/sam/erpik.com/v2026",
  whoami: () => "sam",
  date: () => new Date().toString(),
  echo: (args) => args || "",
  version: () => "Sam Erpik OS v2026.1.0-lts",
};

// ─── AI helper ───────────────────────────────────────────────────────────────
async function askClaude(question, sessionId) {
  const res = await fetch("/.netlify/functions/ask-sam", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question, sessionId }),
  });
  const data = await res.json();
  if (!res.ok) {
    return { text: data.message || "Something went wrong. Try 'hire sam' to reach me directly.", spent: false };
  }
  // `spent` is true only for a real answer — a kill-switch reply made no API call.
  return { text: data.response, spent: !data.killSwitch };
}

// ─── Component ────────────────────────────────────────────────────────────────
// Cycling typewriter for the closed launcher — types invitations on a loop so
// the button reads as a live prompt, not a static badge. Disabled (shows the
// first phrase) when the terminal is open or under reduced-motion.
const TEASE_PROMPTS = [
  "ask Sam anything…",
  "what has Sam built?",
  "is Sam available?",
  "what's Purple Luna?",
];

function useTeaseLoop(enabled) {
  const [out, setOut] = useState(TEASE_PROMPTS[0]);
  useEffect(() => {
    if (!enabled) { setOut(TEASE_PROMPTS[0]); return; }
    let cancelled = false;
    let timer;
    const wait = (ms) => new Promise((r) => { timer = setTimeout(r, ms); });
    (async () => {
      setOut("");
      await wait(500);
      let pi = 0;
      while (!cancelled) {
        const phrase = TEASE_PROMPTS[pi];
        for (let i = 1; i <= phrase.length; i++) { if (cancelled) return; setOut(phrase.slice(0, i)); await wait(55); }
        await wait(1600);
        for (let i = phrase.length; i >= 0; i--) { if (cancelled) return; setOut(phrase.slice(0, i)); await wait(26); }
        await wait(350);
        pi = (pi + 1) % TEASE_PROMPTS.length;
      }
    })();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [enabled]);
  return out;
}

export default function Terminal() {
  const navigate = useNavigate();
  const { muted, toggleMute, playKey } = useTerminalSound();
  const [isOpen, setIsOpen]     = useState(false);
  const reduce = useReducedMotion();
  const tease  = useTeaseLoop(!isOpen && !reduce);
  const [usage, setUsage]       = useState(getUsage);
  const [history, setHistory]   = useState(() => initialHistory(!usage.nudged));
  const [input, setInput]       = useState("");
  const [cmdHistory, setCmdHistory] = useState([]);
  const [cmdHistoryIdx, setCmdHistoryIdx] = useState(-1);
  const [isTyping, setIsTyping] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  // Live UTC clock for the status bar — single setInterval, started only
  // while the terminal is open.
  const [clock, setClock] = useState(() =>
    new Date().toISOString().slice(11, 19) + " UTC"
  );
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  const [vp, setVp] = useState(() => ({
    height: typeof window !== "undefined"
      ? (window.visualViewport?.height || window.innerHeight)
      : null,
    offsetTop: 0,
  }));

  const inputRef     = useRef(null);
  const outputRef    = useRef(null);
  const typingRef    = useRef(null);
  const pendingIdRef = useRef(null);
  const windowRef    = useRef(null);
  const triggerRef   = useRef(null);
  const everOpenedRef = useRef(false);
  const lastKindRef  = useRef(null);

  const aiRemaining = remaining(usage);

  // Tick the status-bar UTC clock once per second, but only while the
  // terminal is open — no work in the background when it isn't visible.
  useEffect(() => {
    if (!isOpen) return;
    const tick = () =>
      setClock(new Date().toISOString().slice(11, 19) + " UTC");
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isOpen]);

  // ` key opens/closes
  useEffect(() => {
    const onKey = (e) => {
      if (
        e.key === "`" && !e.ctrlKey && !e.metaKey && !e.altKey &&
        e.target.tagName !== "INPUT" && e.target.tagName !== "TEXTAREA"
      ) {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Hero terminal click event — and external opens (e.g. /garage handoff)
  // can pass `detail.prefill` to seed a system-line of context so the AI
  // starts the conversation knowing what the visitor was just looking at.
  useEffect(() => {
    const onOpen = (e) => {
      const prefill = e?.detail?.prefill;
      if (typeof prefill === "string" && prefill.trim()) {
        setHistory((h) => [...h, { type: "system", text: prefill.trim() }]);
      }
      setIsOpen(true);
    };
    window.addEventListener("erpik:open-terminal", onOpen);
    return () => window.removeEventListener("erpik:open-terminal", onOpen);
  }, []);

  // Close-time cleanup — and return focus to the trigger so keyboard users
  // land somewhere sensible instead of lost on the page
  useEffect(() => {
    if (isOpen) {
      everOpenedRef.current = true;
      return;
    }
    if (typingRef.current) clearTimeout(typingRef.current);
    setIsTyping(false);
    if (everOpenedRef.current) triggerRef.current?.focus();
  }, [isOpen]);

  // Show the first-open framing nudge only once per browser
  useEffect(() => {
    if (!usage.nudged) setUsage(markNudged());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the input focused — on open and after every reply — so the user can
  // keep typing straight away, no Tab press, no hunting for the field
  useEffect(() => {
    if (isOpen && !isTyping) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [isOpen, isTyping]);

  // Scroll the transcript (not the page) to the latest line
  useEffect(() => {
    const el = outputRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [history]);

  // Track mobile vs desktop
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Follow the visual viewport so the panel sits exactly above the on-screen
  // keyboard — the layout never jumps and the input stays in view
  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) return;
    const sync = () => {
      setVp({ height: vv.height, offsetTop: vv.offsetTop });
      const el = outputRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    sync();
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    return () => {
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
    };
  }, [isOpen]);

  // Lock the page behind the terminal while it's open, and hand the cursor
  // back to the OS — the custom cursor is invisible on the dark terminal
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop?.();
    window.dispatchEvent(new CustomEvent("erpik:cursor-suspend"));
    return () => {
      document.body.style.overflow = prevOverflow;
      window.__lenis?.start?.();
      window.dispatchEvent(new CustomEvent("erpik:cursor-resume"));
    };
  }, [isOpen]);

  const typeText = useCallback((fullText, onDone) => {
    setIsTyping(true);
    let i = 0;
    const id = Symbol();
    setHistory((h) => [...h, { type: "typing", text: "", id }]);

    const tick = () => {
      i++;
      if (i > fullText.length) {
        setHistory((h) =>
          h.map((line) =>
            line.type === "typing" && line.id === id
              ? { type: "output", text: fullText }
              : line
          )
        );
        setIsTyping(false);
        onDone?.();
        return;
      }
      setHistory((h) =>
        h.map((line) =>
          line.type === "typing" && line.id === id
            ? { ...line, text: fullText.slice(0, i) }
            : line
        )
      );
      const ch = fullText[i - 1];
      typingRef.current = setTimeout(tick, ch === "\n" ? 18 : 6);
    };
    typingRef.current = setTimeout(tick, 40);
  }, []);

  // Add a "thinking" spinner entry, return its id so we can replace it later
  const addThinking = useCallback(() => {
    const id = Symbol();
    pendingIdRef.current = id;
    setHistory((h) => [...h, { type: "thinking", text: "", id }]);
    return id;
  }, []);

  const replaceThinking = useCallback((id, text) => {
    setHistory((h) =>
      h.map((line) =>
        line.type === "thinking" && line.id === id
          ? { type: "output", text }
          : line
      )
    );
  }, []);

  const runCommand = useCallback(
    async (raw) => {
      const cmd = raw.trim();
      setHistory((h) => [...h, { type: "command", text: raw }]);
      setCmdHistory((h) => (cmd ? [cmd, ...h.slice(0, 49)] : h));
      setCmdHistoryIdx(-1);
      setInput("");

      if (!cmd) return;
      const lower = cmd.toLowerCase();

      if (lower === "exit") {
        typeText("Goodbye. Come back anytime.", () => {
          setTimeout(() => setIsOpen(false), 400);
        });
        return;
      }

      if (lower === "clear") {
        setHistory(initialHistory(false));
        lastKindRef.current = null;
        return;
      }

      const handler = COMMANDS[lower] || COMMANDS[lower.split(" ")[0]];

      if (handler) {
        const args = cmd.split(" ").slice(1).join(" ");
        // Commands may be sync (return string/object) or async (return a
        // Promise). Promise.resolve() flattens both into the same await path.
        const result = await Promise.resolve(handler(args));

        if (typeof result === "object" && result?.action === "contact") {
          typeText(result.text, () => {
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent("erpik:open-contact", { detail: { subject: "Let's talk" } }));
            }, 800);
          });
        } else if (typeof result === "object" && result?.action === "navigate" && result.to) {
          // Type the "Opening..." line, then navigate and close the terminal.
          typeText(result.text, () => {
            setTimeout(() => {
              navigate(result.to);
              setIsOpen(false);
            }, 500);
          });
        } else {
          typeText(typeof result === "string" ? result : String(result));
        }
        lastKindRef.current = null;
        return;
      }

      // ── Pure greeting → free canned reply, no question spent ─────────────
      if (isPureGreeting(cmd)) {
        if (lastKindRef.current !== "greeting") typeText(COPY.greetingReply);
        lastKindRef.current = "greeting";
        return;
      }

      // ── Low-effort input ("a", "ssssss", "\s") → never reaches the AI, so
      //    it costs nothing. Stays quiet when the same junk repeats.
      if (isLowEffort(cmd)) {
        if (lastKindRef.current !== "lowEffort") typeText(COPY.lowEffort);
        lastKindRef.current = "lowEffort";
        return;
      }

      // ── Daily question limit reached → full notice once, then terse ──────
      if (aiRemaining <= 0) {
        typeText(lastKindRef.current === "limit" ? COPY.limitBrief : COPY.limitReached);
        lastKindRef.current = "limit";
        return;
      }

      // Show thinking state
      const thinkingId = addThinking();
      setIsTyping(true);

      try {
        const { text, spent } = await askClaude(cmd.slice(0, 150), getUsage().sid);
        replaceThinking(thinkingId, text);
        lastKindRef.current = null;
        // Only a real answer spends a question — network errors and the daily
        // kill-switch don't, so the visitor isn't charged for them.
        if (spent) {
          const nextUsage = recordQuestion();
          setUsage(nextUsage);
          window.posthog?.capture("ask_sam_terminal", {
            question: cmd.slice(0, 100),
            questionNumber: nextUsage.count,
          });
        }
      } catch {
        replaceThinking(thinkingId, "Network error. Try 'hire sam' to reach me directly.");
      } finally {
        setIsTyping(false);
      }
    },
    [typeText, addThinking, replaceThinking, aiRemaining, navigate]
  );

  // Compiled at module load — every COMMANDS key + a handful of frequent
  // multi-word phrases the user might tab-complete from.
  const COMMAND_KEYS = Object.keys(COMMANDS);

  const onKeyDown = (e) => {
    // Tactile key-click — fires for every printable keystroke + Enter/Tab.
    // No-op if the user has muted (default) — see useTerminalSound.
    if (e.key.length === 1 || ["Enter", "Tab", "Backspace"].includes(e.key)) {
      playKey();
    }

    if (e.key === "Enter") {
      if (!isTyping) runCommand(input);
      return;
    }

    // TAB → autocomplete. If the typed prefix uniquely matches a command,
    // complete it. If many match, print the candidates as a system line.
    // stopPropagation keeps the overlay's focus-trap from also handling it.
    if (e.key === "Tab" && !e.shiftKey) {
      const prefix = input.trim().toLowerCase();
      if (!prefix) return; // empty input — let the focus trap have it
      e.preventDefault();
      e.stopPropagation();
      const matches = COMMAND_KEYS.filter((c) => c.startsWith(prefix));
      if (matches.length === 1) {
        setInput(matches[0]);
      } else if (matches.length > 1) {
        const list = matches.join("  ");
        setHistory((h) => [...h, { type: "system", text: list }]);
      }
      return;
    }

    if (e.key === "Escape") { setIsOpen(false); return; }

    // `N` toggles mute — only when input is empty so it doesn't eat
    // legitimate typing of words containing "n".
    if (
      (e.key === "n" || e.key === "N") &&
      input === "" && !e.metaKey && !e.ctrlKey && !e.altKey
    ) {
      e.preventDefault();
      toggleMute();
      return;
    }

    // ⌘K / Ctrl+K — clear the scrollback (Wahba-style).
    if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
      e.preventDefault();
      setHistory(initialHistory(false));
      lastKindRef.current = null;
      return;
    }

    // Number-key shortcut for the greeting's example questions — active only
    // while the input is empty and the chips are still the latest thing shown
    if (
      (e.key === "1" || e.key === "2" || e.key === "3") &&
      input === "" && !isTyping &&
      history[history.length - 1]?.type === "greeting"
    ) {
      const q = COPY.examples[Number(e.key) - 1];
      if (q) { e.preventDefault(); runCommand(q); }
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setCmdHistoryIdx((i) => {
        const next = Math.min(i + 1, cmdHistory.length - 1);
        if (next >= 0) setInput(cmdHistory[next]);
        return next;
      });
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCmdHistoryIdx((i) => {
        const next = Math.max(i - 1, -1);
        setInput(next === -1 ? "" : cmdHistory[next] || "");
        return next;
      });
    }
  };

  const close = () => {
    if (typingRef.current) clearTimeout(typingRef.current);
    setIsTyping(false);
    setIsOpen(false);
  };

  // Focus trap — keep Tab / Shift+Tab cycling within the terminal while open
  const handleOverlayKeyDown = (e) => {
    if (e.key !== "Tab" || !windowRef.current) return;
    const list = Array.from(
      windowRef.current.querySelectorAll(
        'button, input, a[href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.disabled);
    if (list.length === 0) return;
    const first = list[0];
    const last  = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <motion.button
        ref={triggerRef}
        onClick={() => setIsOpen(true)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.96 }}
        className={`fixed bottom-6 right-6 z-[100] flex items-center gap-2 h-12 pl-3.5 pr-4 rounded-full bg-[#0D1117] text-[#4ADE80] border border-[#30363D] shadow-[0_4px_24px_rgba(0,0,0,0.5)] hover:border-[#4ADE80]/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ADE80] ${!isOpen && !reduce ? "ask-sam-glow" : ""}`}
        aria-label="Ask Sam — open the AI terminal (or press `)"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        title="Ask Sam  ·  press ` to toggle"
        data-cursor="magnetic"
        data-cursor-label="ASK ↳"
      >
        <span className="font-mono text-[13px] font-bold leading-none">&gt;_</span>
        {isOpen ? (
          <span className="font-mono text-[12px] font-semibold tracking-wide leading-none">Ask Sam</span>
        ) : (
          <span
            aria-hidden="true"
            className="font-mono text-[12px] leading-none"
            style={{ width: 150, overflow: "hidden", whiteSpace: "nowrap", textAlign: "left", color: "#C9D1D9" }}
          >
            {tease}
            {!reduce && (
              <span
                style={{
                  display: "inline-block", width: 6, height: 13, marginLeft: 2,
                  background: "#4ADE80", verticalAlign: "middle",
                  animation: "termBlink 0.9s step-end infinite",
                }}
              />
            )}
          </span>
        )}
      </motion.button>

      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="terminal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[300] flex items-center justify-center md:p-12"
            style={{ background: "rgba(0,0,0,0.82)", backdropFilter: "blur(6px)" }}
            onClick={close}
            onKeyDown={handleOverlayKeyDown}
          >
            <motion.div
              key="terminal-window"
              ref={windowRef}
              role="dialog"
              aria-modal="true"
              aria-label="Ask Sam terminal"
              initial={{ opacity: 0, y: isMobile ? 0 : 32, scale: isMobile ? 1 : 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: isMobile ? 0 : 24, scale: isMobile ? 1 : 0.97 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="relative flex flex-col overflow-hidden w-full max-w-[900px] max-h-[82vh]"
              style={{
                background: "#06120A",
                border: "1px solid #15402B",
                borderRadius: 14,
                boxShadow: "0 48px 100px rgba(0,0,0,0.9), 0 0 60px rgba(74,222,128,0.07), 0 0 0 1px rgba(74,222,128,0.06)",
                ...(isMobile
                  ? {
                      position: "fixed",
                      left: 0,
                      right: 0,
                      top: vp.offsetTop || 0,
                      width: "100%",
                      maxWidth: "100%",
                      height: vp.height ? `${vp.height}px` : "100%",
                      maxHeight: "none",
                      borderRadius: 0,
                      border: "none",
                    }
                  : {}),
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Scan-lines + bloom — sits on top of every layer.
                  pointer-events: none so it never eats a click. */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 z-[5]"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to bottom, transparent 0, transparent 2px, rgba(0,0,0,0.22) 2px, rgba(0,0,0,0.22) 3px)",
                  mixBlendMode: "multiply",
                  opacity: 0.55,
                }}
              />

              {/* Title bar */}
              <div
                className="relative z-[6] flex items-center gap-2 px-4 py-3 shrink-0 select-none"
                style={{
                  background: "#0A1B12",
                  borderBottom: "1px solid #15402B",
                  borderRadius: isMobile ? 0 : "14px 14px 0 0",
                }}
              >
                <button
                  onClick={close}
                  aria-label="Close terminal"
                  className="relative shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ADE80] focus-visible:ring-offset-2 focus-visible:ring-offset-[#161B22]"
                  style={{ width: 12, height: 12 }}
                >
                  {/* enlarged invisible tap target — comfortable on touch */}
                  <span className="absolute" style={{ inset: -12 }} />
                  <span className="block w-3 h-3 rounded-full bg-[#FF5F57] hover:brightness-125 transition-all" />
                </button>
                <span className="w-3 h-3 rounded-full bg-[#FEBC2E] shrink-0" />
                <span className="w-3 h-3 rounded-full bg-[#28C840] shrink-0" />
                <div className="ml-auto flex items-center gap-2.5 min-w-0">
                  <span className="relative flex h-1.5 w-1.5 shrink-0" aria-hidden>
                    <span className="absolute inset-0 rounded-full bg-[#4ADE80] animate-ping opacity-60" />
                    <span className="relative rounded-full h-1.5 w-1.5 bg-[#4ADE80]" />
                  </span>
                  <span className="font-mono text-[11px] tracking-wider truncate" style={{ color: "#6E7681" }}>
                    sam@erpik — bash
                  </span>
                  {aiRemaining > 0 && (
                    <span className="font-mono text-[10px] shrink-0" style={{ color: "#4ADE8055" }}>
                      {counterLabel(aiRemaining)}
                    </span>
                  )}
                  {/* Live UTC clock — the touch the Wahba terminal had */}
                  <span
                    className="font-mono text-[10px] tracking-wider tabular-nums shrink-0 hidden sm:inline"
                    style={{ color: "#4ADE8088" }}
                    aria-label="Current UTC time"
                  >
                    {clock}
                  </span>
                  {/* Mute toggle — default muted, see useTerminalSound */}
                  <button
                    onClick={toggleMute}
                    aria-label={muted ? "Unmute terminal sounds (N)" : "Mute terminal sounds (N)"}
                    aria-pressed={!muted}
                    title={muted ? "Sounds off · press N" : "Sounds on · press N"}
                    className="shrink-0 inline-flex items-center justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ADE80]"
                    style={{
                      width: 22, height: 22,
                      color: muted ? "#6E7681" : "#4ADE80",
                    }}
                    data-cursor="magnetic"
                    data-cursor-label={muted ? "UNMUTE" : "MUTE"}
                  >
                    {muted
                      ? <VolumeX size={13} strokeWidth={2} />
                      : <Volume2 size={13} strokeWidth={2} />
                    }
                  </button>
                </div>
              </div>

              {/* Output */}
              <div
                ref={outputRef}
                className="relative z-[6] flex-1 overflow-y-auto px-5 md:px-7 pt-5 pb-2 space-y-1 font-mono text-[13px] leading-[1.75]"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "#15402B #06120A",
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "contain",
                  // Subtle phosphor glow — sits over the whole reading area.
                  textShadow: "0 0 6px rgba(74,222,128,0.35)",
                }}
                onClick={() => inputRef.current?.focus()}
              >
                {history.map((line, i) => {
                  if (line.type === "command") {
                    return (
                      <div key={i} className="flex gap-2 items-baseline">
                        <span className="shrink-0 font-bold" style={{ color: "#7EE787" }}>{PROMPT}</span>
                        <span style={{ color: "#BBF7D0" }}>{line.text}</span>
                      </div>
                    );
                  }
                  if (line.type === "system") {
                    return (
                      <pre key={i} className="whitespace-pre-wrap font-mono" style={{ color: "#4ADE80", opacity: 0.75, fontFamily: "inherit" }}>
                        {line.text}
                      </pre>
                    );
                  }
                  if (line.type === "greeting") {
                    return (
                      <div key={i} className="space-y-2.5">
                        <pre className="whitespace-pre-wrap font-mono" style={{ color: "#86EFAC", fontFamily: "inherit" }}>
                          {line.text}
                        </pre>
                        <div className="flex flex-wrap gap-2">
                          {COPY.examples.map((q, qi) => (
                            <button
                              key={q}
                              onClick={() => { if (!isTyping) runCommand(q); }}
                              className="inline-flex items-center gap-1.5 font-mono text-[11px] px-2.5 py-1 rounded-md transition-all hover:brightness-125 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ADE80] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1117]"
                              style={{
                                color: "#4ADE80",
                                border: "1px solid #21262D",
                                background: "rgba(74,222,128,0.06)",
                              }}
                            >
                              <span className="font-bold opacity-50">{qi + 1}</span>
                              {q}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  if (line.type === "thinking") {
                    return (
                      <div key={i} className="flex items-center gap-2" style={{ color: "#4ADE80", opacity: 0.6 }}>
                        <span className="font-mono text-[11px]">thinking</span>
                        {[0, 1, 2].map((d) => (
                          <motion.span
                            key={d}
                            animate={{ opacity: [0.2, 1, 0.2] }}
                            transition={{ duration: 1.1, repeat: Infinity, delay: d * 0.2 }}
                            className="w-1 h-1 rounded-full bg-[#4ADE80]"
                          />
                        ))}
                      </div>
                    );
                  }
                  // A reply that points the visitor at "the contact form" is only
                  // useful if there's something to click right there — the AI's
                  // wording varies (it's a live model, not a fixed string), so we
                  // match on intent and attach a real, guaranteed-working action
                  // rather than relying on prose to be actionable.
                  if (line.type === "output" && /contact form/i.test(line.text)) {
                    return (
                      <div key={i} className="space-y-2">
                        <pre className="whitespace-pre-wrap font-mono" style={{ color: "#86EFAC", fontFamily: "inherit" }}>
                          {line.text}
                        </pre>
                        <button
                          onClick={() =>
                            window.dispatchEvent(
                              new CustomEvent("erpik:open-contact", { detail: { subject: "Let's talk" } })
                            )
                          }
                          className="inline-flex items-center gap-1.5 font-mono text-[11px] px-2.5 py-1 rounded-md transition-all hover:brightness-125 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ADE80] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1117]"
                          style={{ color: "#4ADE80", border: "1px solid #21262D", background: "rgba(74,222,128,0.06)" }}
                        >
                          Open contact form →
                        </button>
                      </div>
                    );
                  }

                  // output + typing
                  return (
                    <pre key={i} className="whitespace-pre-wrap font-mono" style={{ color: "#86EFAC", fontFamily: "inherit" }}>
                      {line.text}
                      {line.type === "typing" && (
                        <span
                          className="inline-block w-[9px] h-[14px] ml-[1px] align-middle"
                          style={{ background: "#4ADE80", animation: "termBlink 1s step-end infinite" }}
                        />
                      )}
                    </pre>
                  );
                })}
              </div>

              {/* Input row */}
              <div
                className="relative z-[6] flex items-center gap-3 px-5 md:px-7 py-4 shrink-0 transition-colors"
                style={{
                  borderTop: `1px solid ${inputFocused ? "#4ADE80" : "#15402B"}`,
                  background: inputFocused ? "rgba(74,222,128,0.05)" : "#06120A",
                }}
                onClick={() => inputRef.current?.focus()}
              >
                <span
                  className="font-mono font-bold shrink-0"
                  style={{ color: "#7EE787", fontSize: isMobile ? 15 : 13 }}
                >
                  {PROMPT}
                </span>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  className="flex-1 min-w-0 bg-transparent outline-none font-mono"
                  style={{
                    color: "#BBF7D0",
                    caretColor: "#4ADE80",
                    fontFamily: "inherit",
                    fontSize: isMobile ? 16 : 13, // 16px stops iOS zooming in on focus
                  }}
                  autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                  enterKeyHint="send"
                  placeholder="type a command or ask anything…"
                  aria-label="Terminal input"
                  maxLength={200}
                />
                {isTyping ? (
                  <span className="font-mono text-[11px] tracking-widest shrink-0" style={{ color: "#4ADE80", opacity: 0.5 }}>
                    ...
                  </span>
                ) : (
                  <span
                    className="font-mono text-[10px] tracking-wider shrink-0 select-none"
                    style={{ color: "#4ADE8055" }}
                    aria-hidden="true"
                  >
                    esc
                  </span>
                )}
              </div>

              {/* Bottom keyboard-hint bar — Wahba-style legend so the
                  shortcuts are discoverable without a "help" command. */}
              <div
                className="relative z-[6] hidden md:flex items-center gap-5 px-5 md:px-7 py-2 shrink-0 font-mono text-[10px] tracking-[0.18em] uppercase select-none"
                style={{
                  background: "#06120A",
                  borderTop: "1px solid #15402B",
                  color: "#4ADE80",
                  opacity: 0.6,
                }}
                aria-hidden
              >
                <span><span style={{ color: "#86EFAC" }}>tab</span> complete</span>
                <span><span style={{ color: "#86EFAC" }}>↑/↓</span> history</span>
                <span><span style={{ color: "#86EFAC" }}>⌘K</span> clear</span>
                <span>
                  <span style={{ color: "#86EFAC" }}>n</span>{" "}
                  {muted ? "unmute" : "mute"}
                </span>
                <span className="ml-auto">
                  <span style={{ color: "#86EFAC" }}>esc</span> close
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
